import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  MenuItem, 
  Select, 
  FormControl, 
  Box, 
  Paper, 
  Typography, 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  InputLabel,
  Tooltip,
  Grid,
  TextField,
  Chip,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  IconButton,
  Collapse
} from '@mui/material';
import StyledDataGrid from 'components/atoms/StyledDataGrid';
import { 
  fetchAdminBrokerAccounts, 
  updateBrokerAccount, 
  refreshBrokerAccountLedgerData,
  selectAllAdminBrokerAccounts 
} from 'redux/admin/adminBrokerAccountSlice';
import { enqueueSnackbar } from 'notistack';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { styled } from '@mui/material/styles';

// JSON Editor for broker_data
const JsonTextField = styled(TextField)(({ theme }) => ({
  fontFamily: 'monospace',
  '& .MuiInputBase-input': {
    fontFamily: 'monospace',
  },
}));

// Helper function to format currency values
const formatCurrency = (value, currency = 'USD', includeSymbol = true) => {
  // Convert 'BASE' to 'USD' as BASE is not a valid currency code
  const validCurrency = currency === 'BASE' ? 'USD' : currency;
  
  return new Intl.NumberFormat('en-US', {
    style: includeSymbol ? 'currency' : 'decimal',
    currency: validCurrency,
    minimumFractionDigits: 2
  }).format(value);
};

// Helper function to format dates
const formatDate = (dateString) => {
  if (!dateString) return 'Never';
  
  const date = new Date(dateString);
  const options = { 
    year: 'numeric', 
    month: 'short', 
    day: '2-digit',
    hour: '2-digit', 
    minute: '2-digit',
    second: '2-digit'
  };
  
  return date.toLocaleDateString('en-US', options);
};

// Component to display account balances
const AccountBalances = ({ ledgerData, accountId, onRefresh, refreshing }) => {
  if (!ledgerData || !ledgerData.ledger) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2">No account data available</Typography>
        <Tooltip title="Refresh Account Data">
          <IconButton
            size="small"
            onClick={() => onRefresh(accountId)}
            color="secondary"
            disabled={refreshing}
            sx={{ p: 0.5, ml: 1 }}
          >
            {refreshing ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  // Format the refresh timestamp
  const refreshedAt = formatDate(ledgerData.refreshed_at);

  // Extract currencies (excluding BASE as it's usually a duplicate with different exchange rate)
  const currencies = Object.keys(ledgerData.ledger).filter(curr => curr !== 'BASE');

  return (
    <Box sx={{ width: '100%' }}>
      {/* Simple Cash Balance Display */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>Ledger Details</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          <Typography variant="body1" sx={{ mr: 1 }}>
            Cash balance: {currencies.map((currency, index) => (
              <React.Fragment key={currency}>
                <Box component="span" sx={{ fontWeight: 'bold' }}>{currency}</Box>: {formatCurrency(ledgerData.ledger[currency].cashbalance, currency, false)}
                {index < currencies.length - 1 ? ', ' : ''}
              </React.Fragment>
            ))}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 2, mr: 1 }}>
            (last updated: {refreshedAt})
          </Typography>
          <Tooltip title="Refresh Account Data">
            <IconButton
              size="small"
              onClick={() => onRefresh(accountId)}
              color="secondary"
              disabled={refreshing}
              sx={{ p: 0.5 }}
            >
              {refreshing ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
};

/**
 * BrokerAccountsPage Component
 * Admin page for viewing and managing broker accounts
 */
export default function BrokerAccountsPage() {
  const dispatch = useDispatch();
  const brokerAccounts = useSelector(selectAllAdminBrokerAccounts);
  const loading = useSelector(state => state.adminBrokerAccount.loading);
  const refreshing = useSelector(state => state.adminBrokerAccount.refreshing);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [newBrokerData, setNewBrokerData] = useState('');
  const [brokerDataError, setBrokerDataError] = useState('');
  const [filteredAccounts, setFilteredAccounts] = useState([]);
  const [localFilter, setLocalFilter] = useState({
    status: '',
    broker: '',
    username: '',
    email: ''
  });
  const [expandedAccountId, setExpandedAccountId] = useState(null);

  // Fetch broker accounts on mount
  useEffect(() => {
    dispatch(fetchAdminBrokerAccounts());
  }, [dispatch]);

  // Initialize filtered accounts when broker accounts load
  useEffect(() => {
    if (brokerAccounts && brokerAccounts.length > 0) {
      setFilteredAccounts([...brokerAccounts]);
    }
  }, [brokerAccounts]);

  // Apply filters when localFilter changes
  useEffect(() => {
    if (brokerAccounts && brokerAccounts.length > 0) {
      applyFilters();
    }
  }, [localFilter]);

  // Handle filter input changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setLocalFilter(prev => ({ ...prev, [name]: value }));
  };

  // Apply filters
  const applyFilters = () => {
    let filtered = [...brokerAccounts];
    
    if (localFilter.status) {
      filtered = filtered.filter(account => {
        // Handle case where status might be null or undefined
        const accountStatus = (account.status || '').toLowerCase();
        const filterStatus = localFilter.status.toLowerCase();
        return accountStatus === filterStatus;
      });
    }
    
    if (localFilter.broker) {
      filtered = filtered.filter(account => 
        account.broker_name && 
        account.broker_name.toLowerCase().includes(localFilter.broker.toLowerCase())
      );
    }
    
    if (localFilter.username) {
      const usernameSearch = localFilter.username.toLowerCase();
      filtered = filtered.filter(account => 
        account.username && 
        account.username.toLowerCase().includes(usernameSearch)
      );
    }
    
    if (localFilter.email) {
      const emailSearch = localFilter.email.toLowerCase();
      filtered = filtered.filter(account => {
        // Handle case where email might be missing
        const email = account.email || '';
        return email.toLowerCase().includes(emailSearch);
      });
    }
    
    setFilteredAccounts(filtered);
  };

  // Reset filters
  const handleResetFilters = () => {
    setLocalFilter({
      status: '',
      broker: '',
      username: '',
      email: ''
    });
    // Reset to show all accounts
    setFilteredAccounts([...brokerAccounts]);
  };

  // Open the edit modal
  const handleOpenModal = (account) => {
    setSelectedAccount(account);
    setNewStatus(account.status);
    setNewBrokerData(JSON.stringify(account.broker_data, null, 2));
    setBrokerDataError('');
    setModalOpen(true);
  };

  // Close the edit modal
  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedAccount(null);
    setNewBrokerData('');
    setBrokerDataError('');
  };

  // Handle refresh account data
  const handleRefreshAccountData = (accountId) => {
    dispatch(refreshBrokerAccountLedgerData(accountId))
      .unwrap()
      .then(() => {
        enqueueSnackbar('Account data refreshed successfully', { variant: 'success' });
      })
      .catch((error) => {
        enqueueSnackbar(`Failed to refresh account data: ${error.message}`, { variant: 'error' });
      });
  };

  // Handle broker account update
  const handleUpdateBrokerAccount = () => {
    if (!selectedAccount) return;
    
    // Parse the broker_data JSON
    let parsedBrokerData;
    try {
      parsedBrokerData = JSON.parse(newBrokerData);
      
      // Validate for Interactive Brokers
      if (selectedAccount.broker_name === 'Interactive Brokers') {
        if (!parsedBrokerData.account_number) {
          setBrokerDataError('Interactive Brokers data must include account_number field');
          return;
        }
      }
      
      setBrokerDataError('');
    } catch (error) {
      setBrokerDataError('Invalid JSON format');
      return;
    }
    
    dispatch(updateBrokerAccount({ 
      accountId: selectedAccount.id, 
      accountData: {
        status: newStatus,
        broker_data: parsedBrokerData
      }
    }))
      .unwrap()
      .then(() => {
        enqueueSnackbar('Broker account updated successfully', { variant: 'success' });
        handleCloseModal();
      })
      .catch((error) => {
        enqueueSnackbar(`Failed to update broker account: ${error.message}`, { variant: 'error' });
      });
  };

  // Handle expand row
  const handleExpand = (accountId) => {
    setExpandedAccountId(accountId === expandedAccountId ? null : accountId);
  };

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 3,
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: 'auto'
      }}
    >
      <Typography variant="h6" sx={{ mb: 2 }}>Broker Account Management</Typography>
      
      {/* Filter Controls */}
      <Paper 
        elevation={1} 
        sx={{ 
          p: 2, 
          mb: 3,
          width: '100%'
        }}
      >
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Filter Accounts</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Username"
              name="username"
              value={localFilter.username}
              onChange={handleFilterChange}
              placeholder="Search by username"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Email"
              name="email"
              value={localFilter.email}
              onChange={handleFilterChange}
              placeholder="Search by email"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Broker"
              name="broker"
              value={localFilter.broker}
              onChange={handleFilterChange}
              placeholder="Search by broker name"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                name="status"
                value={localFilter.status}
                onChange={handleFilterChange}
                label="Status"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="active">Active</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={12} md={12}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button 
                variant="outlined" 
                onClick={handleResetFilters}
                size="small"
              >
                Reset
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Accounts Table */}
      <Box sx={{ 
        width: '100%', 
        overflow: 'auto',
        border: '1px solid rgba(224, 224, 224, 1)',
        borderRadius: 1,
      }}>
        <Box sx={{ 
          minWidth: 1200, 
          width: '100%',
        }}>
          {/* Table Header */}
          <Table sx={{ 
            width: '100%',
            tableLayout: 'fixed',
          }} size="small">
            <TableHead sx={{ backgroundColor: (theme) => theme.palette.grey[100] }}>
              <TableRow sx={{ width: '100%' }}>
                <TableCell padding="checkbox" width="50px" sx={{ fontWeight: 'bold' }}></TableCell>
                <TableCell width="100px" sx={{ fontWeight: 'bold' }}>ID</TableCell>
                <TableCell width="200px" sx={{ fontWeight: 'bold' }}>Username</TableCell>
                <TableCell width="250px" sx={{ fontWeight: 'bold' }}>Email</TableCell>
                <TableCell width="150px" sx={{ fontWeight: 'bold' }}>Broker</TableCell>
                <TableCell width="150px" sx={{ fontWeight: 'bold' }}>Account Number</TableCell>
                <TableCell width="150px" sx={{ fontWeight: 'bold' }}>Balance</TableCell>
                <TableCell width="100px" sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell width="200px" sx={{ fontWeight: 'bold' }}>Created</TableCell>
                <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
          </Table>
          
          {/* Loading indicator */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            /* Account rows */
            <Box sx={{ width: '100%' }}>
              {filteredAccounts.length === 0 ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="body1">No broker accounts found. Try adjusting your filters.</Typography>
                </Box>
              ) : (
                filteredAccounts.map(account => (
                  <Box sx={{ width: '100%' }} key={account.id}>
                    <Table sx={{ tableLayout: 'fixed', width: '100%' }} size="small">
                      <TableBody>
                        <TableRow 
                          sx={{ 
                            display: 'flex', 
                            width: '100%',
                            alignItems: 'center',
                            borderBottom: '1px solid rgba(224, 224, 224, 1)',
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.04)'
                            }
                          }}
                        >
                          <TableCell padding="checkbox" sx={{ width: '50px', borderBottom: 'none', flexShrink: 0 }}>
                            <IconButton 
                              size="small" 
                              onClick={() => handleExpand(account.id)}
                              sx={{ padding: '2px' }}
                            >
                              {expandedAccountId === account.id ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                            </IconButton>
                          </TableCell>
                          <TableCell sx={{ width: '100px', borderBottom: 'none', flexShrink: 0 }}>
                            {account.id}
                          </TableCell>
                          <TableCell 
                            sx={{ 
                              width: '200px', 
                              borderBottom: 'none', 
                              flexShrink: 0,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            <Tooltip title={account.username || 'N/A'}>
                              <span>{account.username || 'N/A'}</span>
                            </Tooltip>
                          </TableCell>
                          <TableCell 
                            sx={{ 
                              width: '250px', 
                              borderBottom: 'none', 
                              flexShrink: 0,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            <Tooltip title={account.email || 'N/A'}>
                              <span>{account.email || 'N/A'}</span>
                            </Tooltip>
                          </TableCell>
                          <TableCell sx={{ width: '150px', borderBottom: 'none', flexShrink: 0 }}>
                            {account.broker_name || 'N/A'}
                          </TableCell>
                          <TableCell sx={{ width: '150px', borderBottom: 'none', flexShrink: 0 }}>
                            {account.broker_data?.account_number || 'N/A'}
                          </TableCell>
                          <TableCell sx={{ width: '150px', borderBottom: 'none', flexShrink: 0 }}>
                            {account.ledger_data && account.ledger_data.ledger && account.ledger_data.ledger.BASE ? 
                              (() => {
                                // Extract the actual currency from BASE object or default to USD
                                const baseCurrency = account.ledger_data.ledger.BASE.currency === 'BASE' 
                                  ? 'USD' 
                                  : account.ledger_data.ledger.BASE.currency;
                                return `${baseCurrency}: ${formatCurrency(account.ledger_data.ledger.BASE.cashbalance, baseCurrency, false)}`;
                              })() : 
                              'N/A'}
                          </TableCell>
                          <TableCell sx={{ width: '100px', borderBottom: 'none', flexShrink: 0 }}>
                            <Chip 
                              label={(account.status || 'Unknown').toUpperCase()} 
                              color={account.status === 'active' ? 'success' : 'default'} 
                              size="small" 
                            />
                          </TableCell>
                          <TableCell sx={{ width: '200px', borderBottom: 'none', flexShrink: 0 }}>
                            {new Date(account.created).toLocaleString()}
                          </TableCell>
                          <TableCell sx={{ borderBottom: 'none', flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                            <Button 
                              startIcon={<EditIcon />} 
                              size="small"
                              onClick={() => handleOpenModal(account)}
                              variant="outlined"
                              color="primary"
                            >
                              Edit
                            </Button>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                    <Collapse in={expandedAccountId === account.id} timeout="auto" unmountOnExit>
                      <Box sx={{ px: 4, py: 2, backgroundColor: 'rgba(0, 0, 0, 0.02)' }}>
                        {account.status === 'active' ? (
                          <AccountBalances 
                            ledgerData={account.ledger_data} 
                            accountId={account.id}
                            onRefresh={handleRefreshAccountData}
                            refreshing={refreshing}
                          />
                        ) : (
                          <Typography variant="body2">
                            Account is not active. Activate the account to fetch and view ledger data.
                          </Typography>
                        )}
                      </Box>
                    </Collapse>
                  </Box>
                ))
              )}
            </Box>
          )}
        </Box>
      </Box>

      {/* Edit Broker Account Modal */}
      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle>Edit Broker Account</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {selectedAccount && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1" gutterBottom>
                    <strong>Username:</strong> {selectedAccount.username}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1" gutterBottom>
                    <strong>Email:</strong> {selectedAccount.email}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1" gutterBottom>
                    <strong>Broker:</strong> {selectedAccount.broker_name}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body1" gutterBottom>
                    <strong>Created:</strong> {new Date(selectedAccount.created).toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel id="account-status-label">Status</InputLabel>
                    <Select
                      labelId="account-status-label"
                      value={newStatus}
                      label="Status"
                      onChange={(e) => setNewStatus(e.target.value)}
                    >
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="active">Active</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Broker Data (JSON format)
                  </Typography>
                  <JsonTextField
                    multiline
                    fullWidth
                    minRows={5}
                    maxRows={10}
                    value={newBrokerData}
                    onChange={(e) => setNewBrokerData(e.target.value)}
                    error={!!brokerDataError}
                    helperText={brokerDataError || "For Interactive Brokers, must include account_number field"}
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancel</Button>
          <Button onClick={handleUpdateBrokerAccount} variant="contained" color="secondary">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
} 